package com.example.crashbit

import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.IntSize
import kotlinx.coroutines.*
import kotlinx.coroutines.channels.Channel
import kotlin.random.Random
import kotlin.math.sqrt

data class Snapshot(
    val bodies: List<SpaceBody>,
    val stars: List<SpaceBody>,
    val nebulae: List<Nebula>,
    val randomSeed: Long,
    val gameTimeNanos: Long
)

class UniverseSimulation(
    private val hapticFeedback: HapticFeedback?,
    private val scope: CoroutineScope,
    private val onStateUpdated: (SimulationFrame) -> Unit, // V1.5: Simplified frame sync
    private val onMergerEvent: (MergerEvent) -> Unit
) {
    val bodies = mutableListOf<SpaceBody>()
    val stars = mutableListOf<SpaceBody>()
    var nebulae = listOf<Nebula>()

    var isCrazyEddie = false
    var starsCanMerge = true
    var starsAreDynamic = true
    var planetsCanBeConsumed = true
    var auLimit = 1000f
    var isPaused = false
    
    var pan = Offset.Zero
    var scale = 1.0f
    var screenSize = IntSize.Zero
    var screenCenter = Offset.Zero
    var selectedBodyId: Long? = null
    var isCustomPlacing = false
    var currentScreen: Screen = Screen.Title
    var lastActiveMode: GameMode? = null
    var rainTouchPoint: Offset? = null
    var inputMode: InputMode = InputMode.OFF
    var selectedPlanetSize: PlanetSize = PlanetSize.SMALL

    var stats = TelemetryStats(0f, 0f, 0f, 0, 0, -1)
    var activeEffects = listOf<VisualEffect>()
    var gameTimeNanos = 0L
    var drawVersion = 0
    var trajectories: OracleResult? = null
    var tps = 0f
    var displayStrings = TelemetryDisplayStrings() // V1.6: Performance Cache
    var scannerVersion = 0 // V2.6: Throttled UI state
    
    var timeScale = 1.0f
    private val snapshotBuffer = mutableListOf<Snapshot>()
    private var lastSnapshotTimeMillis = 0L
    
    var isDeterministicMode = false
    var isVibrationEnabled = true
    var hapticIntensity = 0.5f
    private var rng = Random(Random.nextLong())
    private var currentSeed = Random.nextLong()

    private val physicsPool = QuadNodePool(40000)

    fun performRewind() {
        if (snapshotBuffer.isEmpty()) return
        val target = snapshotBuffer.removeAt(0)
        bodies.clear(); bodies.addAll(target.bodies.map { it.copy() })
        stars.clear(); stars.addAll(target.stars.map { it.copy() })
        nebulae = target.nebulae
        if (isDeterministicMode) { currentSeed = target.randomSeed; rng = Random(currentSeed); gameTimeNanos = target.gameTimeNanos }
        lastSnapshotTimeMillis = 0L; resetSignal.trySend(Unit)
    }

    private fun captureSnapshot(localBodies: List<SpaceBody>, localStars: List<SpaceBody>) {
        val now = currentTimeMillis()
        if (now - lastSnapshotTimeMillis >= 1000L) {
            if (snapshotBuffer.size >= 30) snapshotBuffer.removeAt(0)
            val nextSeed = if (isDeterministicMode) rng.nextLong() else Random.nextLong()
            snapshotBuffer.add(Snapshot(bodies = localBodies.map { it.copy() }, stars = localStars.map { it.copy() }, nebulae = nebulae.toList(), randomSeed = currentSeed, gameTimeNanos = gameTimeNanos))
            if (isDeterministicMode) currentSeed = nextSeed
            lastSnapshotTimeMillis = now
        }
    }

    var config = SimulationConfig()
    val spawnQueue = Channel<SpaceBody>(Channel.UNLIMITED)
    val resetSignal = Channel<Unit>(Channel.CONFLATED)
    val loadSignal = Channel<Unit>(Channel.CONFLATED)
    private val cpuBackend = CpuBarnesHutBackend()
    private var lastHapticTime = 0L

    init { startPhysicsLoop(); startSpawnerLoop() }

    fun reset(initialBodies: List<SpaceBody>, initialStars: List<SpaceBody>) {
        while (spawnQueue.tryReceive().isSuccess) {}
        bodies.clear(); bodies.addAll(initialBodies); stars.clear(); stars.addAll(initialStars)
        resetSignal.trySend(Unit)
    }

    fun load(initialBodies: List<SpaceBody>, initialStars: List<SpaceBody>) {
        bodies.clear(); bodies.addAll(initialBodies); stars.clear(); stars.addAll(initialStars)
        loadSignal.trySend(Unit)
    }

    fun updateTrajectories() {
        if (isPaused) {
            trajectories = null
            onStateUpdated(SimulationFrame(bodies.toList(), stars.toList(), stats, displayStrings, scannerVersion, activeEffects.toList(), gameTimeNanos, drawVersion, null, 1.0f))
            val snapshotP = bodies.map { it.copy() }; val snapshotS = stars.map { it.copy() }
            scope.launch(Dispatchers.Default) {
                trajectories = PhysicsEngine.calculateTrajectories(snapshotP, snapshotS, config, physicsPool)
                onStateUpdated(SimulationFrame(snapshotP, snapshotS, stats, displayStrings, scannerVersion, activeEffects, gameTimeNanos, drawVersion, trajectories, 1.0f))
            }
        } else {
            trajectories = null
            onStateUpdated(SimulationFrame(bodies.toList(), stars.toList(), stats, displayStrings, scannerVersion, activeEffects, gameTimeNanos, drawVersion, null, 1.0f))
        }
    }

    fun generateCosmicName(isStar: Boolean): String {
        val prefixes = if (isStar) listOf("Gliese", "Kepler", "Sirius", "Vega", "Alpha", "Proxima", "TRAPPIST") else listOf("Planet", "X", "Zeta", "Epsilon", "Nova", "Terra", "Vesta")
        val prefix = if (isDeterministicMode) prefixes.random(rng) else prefixes.random()
        val num = if (isDeterministicMode) rng.nextInt(100, 1000) else Random.nextInt(100, 1000)
        val suffix = if (isDeterministicMode) ('a'..'g').toList().random(rng) else ('a'..'g').random()
        return if (isStar) "$prefix-$num" else "$prefix-$num$suffix"
    }

    fun generateInitialComposition(isStar: Boolean, mass: Float): Map<String, Float> {
        if (isStar) return mapOf("Hydrogen" to 0.75f, "Helium" to 0.24f, "Metals" to 0.01f)
        val iron = if (isDeterministicMode) (rng.nextFloat() * 0.4f + 0.1f) else (Random.nextFloat() * 0.4f + 0.1f)
        val silicates = if (isDeterministicMode) (rng.nextFloat() * 0.3f + 0.3f) else (Random.nextFloat() * 0.3f + 0.3f)
        val water = if (mass > 500f) (if (isDeterministicMode) (rng.nextFloat() * 0.2f + 0.1f) else (Random.nextFloat() * 0.2f + 0.1f)) else (if (isDeterministicMode) (rng.nextFloat() * 0.09f + 0.01f) else (Random.nextFloat() * 0.09f + 0.01f))
        val carbon = if (isDeterministicMode) (rng.nextFloat() * 0.04f + 0.01f) else (Random.nextFloat() * 0.04f + 0.01f)
        val total = iron + silicates + water + carbon
        return mapOf("Iron" to iron / total, "Silicates" to silicates / total, "Water" to water / total, "Carbon" to carbon / total)
    }

    fun createStar(pos: Offset, velocity: Offset = Offset.Zero) = SpaceBody(id = Random.nextLong(), name = generateCosmicName(true), position = pos, velocity = velocity, mass = 100000f, radius = 120f, color = Color.Yellow, isStar = true, temperature = 5800f, composition = mapOf("Hydrogen" to 0.75f, "Helium" to 0.24f, "Metals" to 0.01f))

    fun createRandomNebula(offset: Offset? = null): Nebula {
        val pos = offset ?: Offset((Random.nextFloat() * 2000 - 1000), (Random.nextFloat() * 2000 - 1000))
        val composition = when (Random.nextInt(0, 3)) { 0 -> mapOf("Water" to 0.8f, "Silicates" to 0.2f); 1 -> mapOf("Iron" to 0.7f, "Carbon" to 0.3f); else -> mapOf("Hydrogen" to 0.9f, "Helium" to 0.1f) }
        val color = when (composition.keys.first()) { "Water" -> Color(0xFF00BFFF).copy(alpha = 0.4f); "Iron" -> Color(0xFFFF4500).copy(alpha = 0.4f); else -> Color(0xFFFFD700).copy(alpha = 0.4f) }
        return Nebula(name = "Nebula-${Random.nextInt(100, 1000)}", position = pos, radius = Random.nextFloat() * 400f + 400f, density = Random.nextFloat() * 0.4f + 0.2f, color = color, composition = composition)
    }

    private fun startSpawnerLoop() {
        scope.launch(Dispatchers.Default) {
            var counter = 0
            while (true) {
                if (inputMode == InputMode.RAIN && rainTouchPoint != null && !isCustomPlacing && !isPaused) {
                    counter++
                    if (counter >= 3) {
                        val worldPos = (rainTouchPoint!! - screenCenter) / scale - pan
                        val jX = if (isDeterministicMode) (rng.nextFloat() * 4.0f - 2.0f) else (Random.nextFloat() * 4.0f - 2.0f)
                        val jY = if (isDeterministicMode) (rng.nextFloat() * 4.0f - 2.0f) else (Random.nextFloat() * 4.0f - 2.0f)
                        val jitter = Offset(jX, jY)
                        val vX = if (isDeterministicMode) (rng.nextFloat() * 300.0f - 150.0f) else (Random.nextFloat() * 300.0f - 150.0f)
                        val vY = if (isDeterministicMode) (rng.nextFloat() * 300.0f - 150.0f) else (Random.nextFloat() * 300.0f - 150.0f)
                        val hue = if (isDeterministicMode) rng.nextFloat() * 360f else Random.nextFloat() * 360f
                        val bodyColor = Color.hsv(hue, 1.0f, 1.0f)
                        val body = SpaceBody(id = Random.nextLong(), name = generateCosmicName(false), position = worldPos + jitter, velocity = Offset(vX, vY), mass = selectedPlanetSize.mass, radius = selectedPlanetSize.radius, color = bodyColor, composition = generateInitialComposition(false, selectedPlanetSize.mass), rotationSpeed = if (isDeterministicMode) (rng.nextFloat() * 60f - 30f) else (Random.nextFloat() * 60f - 30f), hasRings = if (isDeterministicMode) rng.nextFloat() < 0.15f else Random.nextFloat() < 0.15f)
                        spawnQueue.trySend(body); counter = 0
                    }
                } else counter = 0
                delay(16)
            }
        }
    }

    private fun startPhysicsLoop() {
        scope.launch(Dispatchers.Default) {
            val targetTps = 60; val targetFrameTimeNanos = 1_000_000_000L / targetTps
            val localBodies = mutableListOf<SpaceBody>()
            val localStars = mutableListOf<SpaceBody>()
            var lastTpsUpdateMillis = currentTimeMillis()
            var tickCount = 0
            var lastTimeNanos = currentTimeNanos()
            var accumulatorNanos = 0.0

            while (true) {
                val frameStartNanos = currentTimeNanos()
                val elapsedNanos = frameStartNanos - lastTimeNanos
                lastTimeNanos = frameStartNanos
                
                if (!isPaused) {
                    accumulatorNanos += elapsedNanos.toDouble() * timeScale.toDouble()
                }

                if (resetSignal.tryReceive().isSuccess) { 
                    localBodies.clear(); localBodies.addAll(bodies); localStars.clear(); localStars.addAll(stars)
                    onStateUpdated(SimulationFrame(localBodies.toList(), localStars.toList(), stats, displayStrings, scannerVersion, activeEffects, gameTimeNanos, drawVersion, trajectories, 1.0f))
                    accumulatorNanos = 0.0; continue 
                }
                if (loadSignal.tryReceive().isSuccess) { localBodies.clear(); localBodies.addAll(bodies); localStars.clear(); localStars.addAll(stars); accumulatorNanos = 0.0 }
                
                while (true) { val next = spawnQueue.tryReceive().getOrNull() ?: break; localBodies.add(next) }
                if ((isCustomPlacing || stars.size > localStars.size) && stars.size != localStars.size) { localStars.clear(); localStars.addAll(stars) }

                val fixedDt = 1.0f / 60.0f
                val stepNanos = 1_000_000_000.0 / 60.0
                var simulationOccurred = false

                while (accumulatorNanos >= stepNanos) {
                    try {
                        // V1.5.7: Snapshot previous state for LERP (Deep copy via Offset constructor)
                        localBodies.forEach { it.previousPosition = Offset(it.position.x, it.position.y); it.previousRotation = it.rotation }
                        localStars.forEach { it.previousPosition = Offset(it.position.x, it.position.y); it.previousRotation = it.rotation }

                        val events = if (localStars.isNotEmpty() && !isCustomPlacing && currentScreen is Screen.Sandbox && !isPaused) {
                            cpuBackend.applyPhysics(localBodies, localStars, nebulae, fixedDt, config, physicsPool, isStableMode = (lastActiveMode == GameMode.STABLE_SYSTEM), rng = if (isDeterministicMode) rng else null)
                        } else emptyList()
                        
                        gameTimeNanos += (stepNanos).toLong()
                        processMergerEvents(events)
                        tickCount++; simulationOccurred = true

                        // V1.6: Throttled String Formatting (Every 30 steps ~ 0.5s)
                        if (tickCount % 30 == 0) {
                            val currentStats = calculateStats(localBodies, localStars)
                            displayStrings = TelemetryDisplayStrings(
                                speed = "${(currentStats.fastestSpeed * 10).toInt() / 10.0} km/s",
                                mass = formatMassInternal(currentStats.largestPlanetMass),
                                system = formatMassInternal(currentStats.totalSystemMass)
                            )
                            scannerVersion++ // Force HUD refresh
                        }
                    } catch (e: Exception) {}
                    accumulatorNanos -= stepNanos
                    
                    if (accumulatorNanos > stepNanos * 5) accumulatorNanos = stepNanos
                }

                // V1.5.7: Calculate Interpolation Alpha
                val interpolationAlpha = (accumulatorNanos / stepNanos).toFloat().coerceIn(0f, 1f)

                if (simulationOccurred || interpolationAlpha > 0.01f) {
                    stats = calculateStats(localBodies, localStars)
                    captureSnapshot(localBodies, localStars)
                    if (!isPaused || localStars.isNotEmpty()) {
                        this@UniverseSimulation.bodies.clear(); this@UniverseSimulation.bodies.addAll(localBodies)
                        this@UniverseSimulation.stars.clear(); this@UniverseSimulation.stars.addAll(localStars)
                    }
                    drawVersion++
                    
                    val nowGameMs = gameTimeNanos / 1_000_000
                    localStars.forEach { star ->
                        val bhBurstRoll = if (isDeterministicMode) rng.nextFloat() else Random.nextFloat()
                        if (star.isBlackHole && bhBurstRoll < 0.005f) { activeEffects = activeEffects + VisualEffect(type = EffectType.ACCRETION_BURST, position = star.position, startTime = nowGameMs, durationMs = 1500L, intensity = star.mass / 1000000f, color = Color(0xFF8B00FF), parentId = star.id) }
                        else if (!star.isBlackHole) {
                            val spawnChance = if (star.mass < 200000f) 0.008f else if (star.mass < 500000f) 0.002f else 0.004f
                            if ((if (isDeterministicMode) rng.nextFloat() else Random.nextFloat()) < spawnChance && activeEffects.count { it.type == EffectType.SOLAR_FLARE } < 8) {
                                val baseColor = if (star.mass < 200000f) Color(0xFFADD8E6) else if (star.mass < 500000f) Color.Yellow else if (star.mass < 800000f) Color(0xFFFF8C00) else Color.Red
                                val tierRoll = if (isDeterministicMode) rng.nextFloat() else Random.nextFloat()
                                val (tierColor, dur) = if (tierRoll < 0.01) Color.Magenta to 6000L else if (tierRoll < 0.06) Color.White to 4000L else baseColor to 2500L
                                activeEffects = activeEffects + VisualEffect(type = EffectType.SOLAR_FLARE, position = star.position, startTime = nowGameMs, durationMs = dur, intensity = if (isDeterministicMode) rng.nextFloat() * 360f else (Random.nextFloat() * 360f), color = tierColor, parentId = star.id)
                            }
                        }
                    }
                    if (activeEffects.isNotEmpty()) activeEffects = activeEffects.filter { nowGameMs - it.startTime < it.durationMs }
                    
                    onStateUpdated(SimulationFrame(localBodies.toList(), localStars.toList(), stats, displayStrings, scannerVersion, activeEffects, gameTimeNanos, drawVersion, trajectories, interpolationAlpha))
                }

                if (currentTimeMillis() - lastTpsUpdateMillis >= 1000L) { tps = tickCount.toFloat(); tickCount = 0; lastTpsUpdateMillis = currentTimeMillis() }

                val workTimeNanos = currentTimeNanos() - frameStartNanos
                val sleepTimeMs = (targetFrameTimeNanos - workTimeNanos) / 1_000_000L
                if (sleepTimeMs > 0) delay(sleepTimeMs) else yield()
            }
        }
    }

    private fun calculateStats(currentBodies: List<SpaceBody>, currentStars: List<SpaceBody>): TelemetryStats {
        var maxSpeed = 0f; var maxMass = 0f; var totalMass = 0f; var starsCount = 0; var bhCount = 0; var fastestId = -1; var largestId = -1
        currentBodies.forEachIndexed { index, body -> val speed = body.velocity.getDistance(); if (speed > maxSpeed) { maxSpeed = speed; fastestId = index }; if (body.mass > maxMass) { maxMass = body.mass; largestId = index }; totalMass += body.mass }
        currentStars.forEach { totalMass += it.mass; if (it.isBlackHole) bhCount++ else starsCount++ }
        return TelemetryStats(maxSpeed, maxMass, totalMass, starsCount, bhCount, fastestId, largestId)
    }

    private fun processMergerEvents(events: List<MergerEvent>) {
        if (events.isEmpty()) return
        val nowGameMs = gameTimeNanos / 1_000_000; val newEffects = mutableListOf<VisualEffect>()
        val heavyTypes = setOf(EffectType.BLACK_HOLE_COLLAPSE, EffectType.STAR_COLLAPSE, EffectType.SUPERNOVA_SHOCKWAVE)
        var heavyCount = activeEffects.count { it.type in heavyTypes }
        events.forEach { event ->
            onMergerEvent(event); triggerHaptic(event.totalMass)
            if (heavyCount < 2) {
                if (event.didCreateBlackHole) { newEffects.add(VisualEffect(type = EffectType.BLACK_HOLE_COLLAPSE, position = event.position, startTime = nowGameMs, durationMs = 8000L, intensity = 5.0f, color = Color.Magenta)); newEffects.add(VisualEffect(type = EffectType.SUPERNOVA_SHOCKWAVE, position = event.position, startTime = nowGameMs, durationMs = 4000L, intensity = 2.0f, color = Color.Magenta)); heavyCount++ }
                else if (event.didCreateStar || event.isStellarEvent) { val color = if (event.didCreateStar) Color.Yellow else Color.White; newEffects.add(VisualEffect(type = EffectType.STAR_COLLAPSE, position = event.position, startTime = nowGameMs, durationMs = 5000L, intensity = 3.0f, color = color)); newEffects.add(VisualEffect(type = EffectType.SUPERNOVA_SHOCKWAVE, position = event.position, startTime = nowGameMs, durationMs = 3000L, intensity = 1.0f, color = color)); heavyCount++ }
            }
            val relPos = (event.position + pan) * scale + screenCenter
            if (relPos.x < 0 || relPos.x > screenSize.width || relPos.y < 0 || relPos.y > screenSize.height) newEffects.add(VisualEffect(type = EffectType.OFFSCREEN_MERGER, position = event.position, startTime = nowGameMs, durationMs = 500L, color = Color.Red))
            if (newEffects.count { it.type == EffectType.GRAVITATIONAL_WAVE } < 3) newEffects.add(VisualEffect(type = EffectType.GRAVITATIONAL_WAVE, position = event.position, startTime = nowGameMs, durationMs = 3000L, intensity = (event.totalMass / 100f).coerceAtMost(100f)))
        }
        if (newEffects.isNotEmpty()) activeEffects = (activeEffects + newEffects).takeLast(30)
    }

    fun triggerHaptic(mass: Float) {
        if (!isVibrationEnabled) return
        val now = currentTimeMillis(); if (now - lastHapticTime < 60) return
        lastHapticTime = now; hapticFeedback?.triggerHaptic(mass, hapticIntensity)
    }

    private fun formatMassInternal(mass: Float): String {
        return when {
            mass >= 1e12f -> "${(mass / 1e11f).toInt() / 10.0}T T"
            mass >= 1e9f -> "${(mass / 1e8f).toInt() / 10.0}B T"
            mass >= 1e6f -> "${(mass / 1e5f).toInt() / 10.0}M T"
            else -> "${mass.toInt()}T"
        }
    }
}
